import { create } from 'zustand';
import { api, Board, Card, Column, Tag, User, Activity, Comment } from '../api/client';
import { useToastStore } from './toastStore';
import {
  clearStoredBoardId,
  getBoardIdFromLocation,
  getStoredBoardId,
  pushBoardUrl,
  replaceBoardUrl,
  storeBoardId,
} from '../utils/boardRouting';

interface BoardListItem extends Board {
  is_owner: boolean;
}

interface BoardState {
  boards: BoardListItem[];
  board: Board | null;
  tags: Tag[];
  currentUser: User | null;
  activity: Activity[];
  isLoading: boolean;
  error: string | null;

  selectedCardId: string | null;
  selectedCardContent: string | null;
  selectedCardComments: Comment[];

  searchQuery: string;
  searchResults: Card[] | null;

  fetchBoards: () => Promise<void>;
  fetchBoard: (id?: string) => Promise<Board>;
  initializeBoard: () => Promise<void>;
  syncBoardFromLocation: () => Promise<void>;
  refreshBoard: () => Promise<void>;
  createBoard: (name: string) => Promise<Board>;
  deleteBoard: (id: string) => Promise<void>;
  switchBoard: (id: string) => Promise<void>;
  fetchTags: (boardId?: string) => Promise<void>;
  fetchCurrentUser: () => Promise<void>;
  fetchActivity: (boardId?: string) => Promise<void>;

  createColumn: (data: { name: string; color: string }) => Promise<Column>;
  updateColumn: (id: string, data: { name?: string; color?: string }) => Promise<void>;
  reorderColumn: (id: string, direction: 'left' | 'right') => Promise<void>;
  deleteColumn: (id: string, destinationColumnId?: string) => Promise<void>;

  createCard: (data: { title: string; description?: string; column_id: string; content: string; color_label?: string }) => Promise<Card>;
  updateCard: (id: string, data: { title?: string; description?: string; color_label?: string | null }) => Promise<void>;
  updateCardContent: (id: string, content: string) => Promise<void>;
  moveCard: (id: string, columnId: string, position: number) => Promise<void>;
  deleteCard: (id: string) => Promise<void>;
  duplicateCard: (id: string) => Promise<void>;
  createCardFromMarkdown: (file: File, columnId: string) => Promise<void>;
  addTagToCard: (cardId: string, tagId: string) => Promise<void>;
  removeTagFromCard: (cardId: string, tagId: string) => Promise<void>;

  createTag: (data: { name: string; color: string }) => Promise<Tag>;
  updateTag: (id: string, data: { name?: string; color?: string }) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;

  fetchCardDetails: (cardId: string) => Promise<void>;
  addComment: (cardId: string, content: string) => Promise<void>;
  deleteComment: (id: string) => Promise<void>;

  selectCard: (cardId: string | null) => void;
  setSearchQuery: (query: string) => void;
  search: (query: string) => Promise<void>;
  clearSearch: () => void;

  updateCardInBoard: (card: Card) => void;
}

function getNextBoard(boards: BoardListItem[], deletedBoardId: string) {
  return boards.find((board) => board.id !== deletedBoardId) || null;
}

export const useBoardStore = create<BoardState>((set, get) => ({
  boards: [],
  board: null,
  tags: [],
  currentUser: null,
  activity: [],
  isLoading: false,
  error: null,
  selectedCardId: null,
  selectedCardContent: null,
  selectedCardComments: [],
  searchQuery: '',
  searchResults: null,

  fetchBoards: async () => {
    try {
      const boards = await api.listBoards();
      set({ boards });
    } catch (error) {
      console.error('Failed to fetch boards:', error);
    }
  },

  fetchBoard: async (id?: string) => {
    set({ isLoading: true, error: null });

    try {
      const board = await api.getBoard(id);
      storeBoardId(board.id);
      set({
        board,
        isLoading: false,
        error: null,
        selectedCardId: null,
        selectedCardContent: null,
        selectedCardComments: [],
      });
      return board;
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  initializeBoard: async () => {
    const requestedBoardId = getBoardIdFromLocation() ?? getStoredBoardId() ?? undefined;

    try {
      const board = await get().fetchBoard(requestedBoardId);
      replaceBoardUrl(board.id);
      await Promise.all([
        get().fetchTags(board.id),
        get().fetchCurrentUser(),
        get().fetchActivity(board.id),
      ]);
    } catch (error) {
      if (!requestedBoardId) {
        await get().fetchCurrentUser();
        return;
      }

      clearStoredBoardId();

      try {
        const board = await get().fetchBoard();
        replaceBoardUrl(board.id);
        await Promise.all([
          get().fetchTags(board.id),
          get().fetchCurrentUser(),
          get().fetchActivity(board.id),
        ]);
      } catch {
        await get().fetchCurrentUser();
      }
    }
  },

  syncBoardFromLocation: async () => {
    const requestedBoardId = getBoardIdFromLocation() ?? undefined;

    if (!requestedBoardId) {
      const activeBoardId = get().board?.id;
      if (activeBoardId) {
        replaceBoardUrl(activeBoardId);
        return;
      }

      await get().initializeBoard();
      return;
    }

    if (requestedBoardId === get().board?.id) {
      return;
    }

    try {
      const board = await get().fetchBoard(requestedBoardId);
      await Promise.all([
        get().fetchTags(board.id),
        get().fetchActivity(board.id),
      ]);
    } catch {
      try {
        const board = await get().fetchBoard();
        replaceBoardUrl(board.id);
        await Promise.all([
          get().fetchTags(board.id),
          get().fetchActivity(board.id),
        ]);
      } catch {
        // Keep existing error state. There's nothing better to do here.
      }
    }
  },

  refreshBoard: async () => {
    const activeBoardId = get().board?.id;

    if (!activeBoardId) {
      await get().initializeBoard();
      return;
    }

    const board = await get().fetchBoard(activeBoardId);
    await Promise.all([
      get().fetchTags(board.id),
      get().fetchActivity(board.id),
    ]);
  },

  createBoard: async (name: string) => {
    const newBoard = await api.createBoard(name);
    set({ boards: [{ ...newBoard, is_owner: true }, ...get().boards] });
    return newBoard;
  },

  deleteBoard: async (id: string) => {
    await api.deleteBoard(id);

    const boards = get().boards.filter((board) => board.id !== id);
    const wasActiveBoard = get().board?.id === id;
    set({ boards });

    if (!wasActiveBoard) {
      return;
    }

    const nextBoard = getNextBoard(boards, id);

    if (!nextBoard) {
      clearStoredBoardId();
      set({ board: null, tags: [], activity: [] });
      return;
    }

    const board = await get().fetchBoard(nextBoard.id);
    replaceBoardUrl(board.id);
    await Promise.all([
      get().fetchTags(board.id),
      get().fetchActivity(board.id),
    ]);
  },

  switchBoard: async (id: string) => {
    const board = await get().fetchBoard(id);
    pushBoardUrl(board.id);
    await Promise.all([
      get().fetchTags(board.id),
      get().fetchActivity(board.id),
    ]);
  },

  fetchTags: async (boardId?: string) => {
    const activeBoardId = boardId ?? get().board?.id;

    if (!activeBoardId) {
      set({ tags: [] });
      return;
    }

    try {
      const tags = await api.getTags(activeBoardId);
      set({ tags });
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      set({ tags: [] });
    }
  },

  fetchCurrentUser: async () => {
    try {
      const currentUser = await api.getCurrentUser();
      set({ currentUser });
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  },

  fetchActivity: async (boardId?: string) => {
    const activeBoardId = boardId ?? get().board?.id;

    if (!activeBoardId) {
      set({ activity: [] });
      return;
    }

    try {
      const activity = await api.getActivity(activeBoardId, 20);
      set({ activity });
    } catch (error) {
      console.error('Failed to fetch activity:', error);
      set({ activity: [] });
    }
  },

  createColumn: async (data) => {
    const board = get().board;
    if (!board) throw new Error('No board active');

    const column = await api.createColumn({
      board_id: board.id,
      name: data.name,
      color: data.color,
    });

    set({
      board: {
        ...board,
        columns: [...board.columns, { ...column, cards: [] }].sort((a, b) => a.position - b.position),
      },
    });

    return { ...column, cards: [] };
  },

  updateColumn: async (id, data) => {
    const { board } = get();
    if (!board) return;

    const updatedColumn = await api.updateColumn(id, data);

    set({
      board: {
        ...board,
        columns: board.columns
          .map((column) =>
            column.id === id
              ? {
                  ...column,
                  name: updatedColumn.name,
                  color: updatedColumn.color,
                  position: updatedColumn.position,
                }
              : column
          )
          .sort((a, b) => a.position - b.position),
      },
    });
  },

  reorderColumn: async (id, direction) => {
    const { board } = get();
    if (!board) return;

    const currentIndex = board.columns.findIndex((column) => column.id === id);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= board.columns.length) return;

    const reorderedColumns = [...board.columns];
    const [movedColumn] = reorderedColumns.splice(currentIndex, 1);
    reorderedColumns.splice(targetIndex, 0, movedColumn);

    const previousPositions = new Map(board.columns.map((column) => [column.id, column.position]));
    const normalizedColumns = reorderedColumns.map((column, index) => ({
      ...column,
      position: index,
    }));

    set({
      board: {
        ...board,
        columns: normalizedColumns,
      },
    });

    try {
      await Promise.all(
        normalizedColumns
          .filter((column) => previousPositions.get(column.id) !== column.position)
          .map((column) => api.updateColumn(column.id, { position: column.position }))
      );
    } catch (error) {
      set({ board, error: (error as Error).message });
      throw error;
    }
  },

  deleteColumn: async (id, destinationColumnId) => {
    const { board } = get();
    if (!board) return;

    await api.deleteColumn(id, destinationColumnId);
    await Promise.all([
      get().fetchBoard(board.id),
      get().fetchActivity(board.id),
    ]);
  },

  createCard: async (data) => {
    const { board } = get();
    if (!board) throw new Error('No board active');

    try {
      const card = await api.createCard(data);
      set({
        board: {
          ...board,
          columns: board.columns.map((col) =>
            col.id === data.column_id
              ? { ...col, cards: [...col.cards, card] }
              : col
          ),
        },
      });
      void get().fetchActivity(board.id);
      useToastStore.getState().addToast({ type: 'success', message: `"${card.title}" created` });
      return card;
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  createCardFromMarkdown: async (file, columnId) => {
    try {
      const text = await file.text();
      let title = file.name.replace(/\.md$/i, '');
      let content = text;

      const h1Match = text.match(/^#\s+(.+)$/m);
      if (h1Match) {
        title = h1Match[1].trim();
      }

      const previewText = text
        .replace(/^#\s+.+$/m, '')
        .replace(/(?:^|\s)#([a-zA-Z0-9_-]+)/g, '')
        .replace(/\n+/g, ' ')
        .trim();

      const description = previewText.length > 150
        ? previewText.substring(0, 147) + '...'
        : previewText;

      const tagMatches = text.match(/(?:^|\s)#([a-zA-Z0-9_-]+)/g);
      const tagNames = tagMatches
        ? [...new Set(tagMatches.map((match) => match.trim().substring(1)))]
        : [];

      const card = await get().createCard({
        title,
        description: description || undefined,
        column_id: columnId,
        content,
      });

      const { tags, createTag, addTagToCard } = get();
      for (const name of tagNames) {
        let tag = tags.find((existingTag) => existingTag.name.toLowerCase() === name.toLowerCase());
        if (!tag) {
          tag = await createTag({ name, color: '#E5E7EB' });
        }
        await addTagToCard(card.id, tag.id);
      }
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  updateCard: async (id, data) => {
    try {
      const card = await api.updateCard(id, data);
      get().updateCardInBoard(card);
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  updateCardContent: async (id, content) => {
    try {
      await api.updateCardContent(id, content);
      set({ selectedCardContent: content });
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  moveCard: async (id, columnId, position) => {
    const { board } = get();
    if (!board) return;

    let movedCard: Card | null = null;
    let sourceColumnId: string | null = null;

    for (const col of board.columns) {
      const card = col.cards.find((currentCard) => currentCard.id === id);
      if (card) {
        movedCard = card;
        sourceColumnId = col.id;
        break;
      }
    }

    if (!movedCard || !sourceColumnId) return;

    const newBoard = { ...board };
    newBoard.columns = board.columns.map((col) => {
      if (col.id === sourceColumnId) {
        return { ...col, cards: col.cards.filter((currentCard) => currentCard.id !== id) };
      }

      if (col.id === columnId) {
        const newCards = [...col.cards];
        newCards.splice(position, 0, { ...movedCard, column_id: columnId, position });
        return {
          ...col,
          cards: newCards.map((currentCard, index) => ({ ...currentCard, position: index })),
        };
      }

      return col;
    });

    set({ board: newBoard });

    try {
      await api.moveCard(id, { column_id: columnId, position });
      void get().fetchActivity(board.id);
    } catch (error) {
      set({ board, error: (error as Error).message });
    }
  },

  deleteCard: async (id) => {
    const { board, selectedCardId } = get();
    if (!board) return;

    const previousBoard = board;
    const cardTitle = board.columns.flatMap((c) => c.cards).find((c) => c.id === id)?.title || 'Document';

    set({
      board: {
        ...board,
        columns: board.columns.map((col) => ({
          ...col,
          cards: col.cards.filter((card) => card.id !== id),
        })),
      },
      selectedCardId: selectedCardId === id ? null : selectedCardId,
    });

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;
      try {
        await api.deleteCard(id);
        void get().fetchActivity(board.id);
      } catch (error) {
        set({ board: previousBoard, error: (error as Error).message });
      }
    }, 5000);

    useToastStore.getState().addToast({
      type: 'success',
      message: `"${cardTitle}" deleted`,
      duration: 5000,
      undoAction: () => {
        cancelled = true;
        clearTimeout(timer);
        set({ board: previousBoard });
      },
    });
  },

  duplicateCard: async (id) => {
    const { board } = get();
    if (!board) return;

    try {
      let originalCard: Card | null = null;
      for (const col of board.columns) {
        const currentCard = col.cards.find((card) => card.id === id);
        if (currentCard) {
          originalCard = currentCard;
          break;
        }
      }

      if (!originalCard) return;

      const { content } = await api.getCardContent(id);

      await get().createCard({
        title: `${originalCard.title} (Copy)`,
        description: originalCard.description || undefined,
        column_id: originalCard.column_id,
        content,
        color_label: originalCard.color_label || undefined,
      });
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  addTagToCard: async (cardId, tagId) => {
    const { board, tags } = get();
    if (!board) return;

    try {
      await api.addTagToCard(cardId, tagId);
      const tag = tags.find((currentTag) => currentTag.id === tagId);
      if (tag) {
        set({
          board: {
            ...board,
            columns: board.columns.map((col) => ({
              ...col,
              cards: col.cards.map((card) =>
                card.id === cardId ? { ...card, tags: [...card.tags, tag] } : card
              ),
            })),
          },
        });
      }
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  removeTagFromCard: async (cardId, tagId) => {
    const { board } = get();
    if (!board) return;

    try {
      await api.removeTagFromCard(cardId, tagId);
      set({
        board: {
          ...board,
          columns: board.columns.map((col) => ({
            ...col,
            cards: col.cards.map((card) =>
              card.id === cardId ? { ...card, tags: card.tags.filter((tag) => tag.id !== tagId) } : card
            ),
          })),
        },
      });
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  createTag: async (data) => {
    const boardId = get().board?.id;
    if (!boardId) throw new Error('No board active');

    const tag = await api.createTag({ board_id: boardId, ...data });
    set({ tags: [...get().tags, tag].sort((a, b) => a.name.localeCompare(b.name)) });
    return tag;
  },

  updateTag: async (id, data) => {
    const tag = await api.updateTag(id, data);
    set({ tags: get().tags.map((currentTag) => (currentTag.id === id ? tag : currentTag)) });
  },

  deleteTag: async (id) => {
    const activeBoardId = get().board?.id;
    await api.deleteTag(id);
    set({ tags: get().tags.filter((tag) => tag.id !== id) });

    if (activeBoardId) {
      await Promise.all([
        get().fetchBoard(activeBoardId),
        get().fetchTags(activeBoardId),
      ]);
    }
  },

  fetchCardDetails: async (cardId) => {
    try {
      const [{ content }, comments] = await Promise.all([
        api.getCardContent(cardId),
        api.getComments(cardId),
      ]);
      set({ selectedCardContent: content, selectedCardComments: comments });
    } catch (error) {
      console.error('Failed to fetch card details:', error);
    }
  },

  addComment: async (cardId, content) => {
    try {
      const comment = await api.addComment(cardId, content);
      set({ selectedCardComments: [...get().selectedCardComments, comment] });

      const { board } = get();
      if (board) {
        set({
          board: {
            ...board,
            columns: board.columns.map((col) => ({
              ...col,
              cards: col.cards.map((card) =>
                card.id === cardId ? { ...card, comment_count: card.comment_count + 1 } : card
              ),
            })),
          },
        });
        void get().fetchActivity(board.id);
      }
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  deleteComment: async (id) => {
    const { selectedCardComments, board, selectedCardId } = get();

    try {
      await api.deleteComment(id);
      set({ selectedCardComments: selectedCardComments.filter((comment) => comment.id !== id) });

      if (board && selectedCardId) {
        set({
          board: {
            ...board,
            columns: board.columns.map((col) => ({
              ...col,
              cards: col.cards.map((card) =>
                card.id === selectedCardId
                  ? { ...card, comment_count: Math.max(0, card.comment_count - 1) }
                  : card
              ),
            })),
          },
        });
      }
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  selectCard: (cardId) => {
    set({
      selectedCardId: cardId,
      selectedCardContent: null,
      selectedCardComments: [],
    });

    if (cardId) {
      void get().fetchCardDetails(cardId);
    }
  },

  setSearchQuery: (query) => set({ searchQuery: query }),

  search: async (query) => {
    if (!query.trim()) {
      set({ searchResults: null });
      return;
    }

    try {
      const results = await api.getCards({
        board_id: get().board?.id,
        q: query,
      });
      set({ searchResults: results });
    } catch (error) {
      console.error('Search failed:', error);
    }
  },

  clearSearch: () => set({ searchQuery: '', searchResults: null }),

  updateCardInBoard: (card) => {
    const { board } = get();
    if (!board) return;

    set({
      board: {
        ...board,
        columns: board.columns.map((col) => ({
          ...col,
          cards: col.cards.map((currentCard) => (currentCard.id === card.id ? card : currentCard)),
        })),
      },
    });
  },
}));
